import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Space, Popconfirm, Card, Typography, Select, Input, Tag, Drawer, Descriptions, Divider, Tabs, Statistic, Row, Col, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined, CopyOutlined, AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';

const { Title, Text, Paragraph } = Typography;

const { TextArea } = Input;

const Presets = () => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // 搜索/筛选状态
  const [searchText, setSearchText] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [isPublicFilter, setIsPublicFilter] = useState(null);
  const [approvalStatusFilter, setApprovalStatusFilter] = useState(null);

  // 预览抽屉
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
  const [previewPreset, setPreviewPreset] = useState(null);

  // 克隆弹窗
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloningPreset, setCloningPreset] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);

  // 拒绝弹窗
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectingPreset, setRejectingPreset] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // 审批相关状态
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (approvalStatusFilter) {
        params.approval_status = approvalStatusFilter;
      }
      const response = await adminAPI.getAdminPresets(params);
      if (response.code === 0 || response.presets) {
        setPresets(response.presets || response.data?.presets || []);
        setPagination({
          current: response.page || page,
          pageSize: response.pageSize || pageSize,
          total: response.total || 0,
        });
      }
    } catch (error) {
      console.error('获取预设列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    fetchPresets(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleDeletePreset = async (id) => {
    try {
      const response = await adminAPI.deleteAdminPreset(id);
      if (response.code === 0) {
        message.success(t('preset_deleted'));
        fetchPresets(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || t('delete_failed'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('delete_failed'));
    }
  };

  const handleClonePreset = async () => {
    if (!cloneName.trim()) {
      message.error(t('preset_name_required'));
      return;
    }

    setCloning(true);
    try {
      const response = await adminAPI.clonePreset(cloningPreset.id, cloneName);
      if (response.code === 0) {
        message.success(t('preset_cloned'));
        setCloneModalVisible(false);
        setCloneName('');
        setCloningPreset(null);
        fetchPresets(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || t('clone_failed'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('clone_failed'));
    } finally {
      setCloning(false);
    }
  };

  // 审核通过
  const handleApprove = async (preset) => {
    setApproving(preset.id);
    try {
      const response = await adminAPI.approvePreset(preset.id);
      if (response.code === 0) {
        message.success(t('approve_success') || '审核通过成功');
        fetchPresets(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || t('approve_failed') || '审核通过失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('approve_failed') || '审核通过失败');
    } finally {
      setApproving(null);
    }
  };

  // 打开拒绝弹窗
  const openRejectModal = (preset) => {
    setRejectingPreset(preset);
    setRejectNote('');
    setRejectModalVisible(true);
  };

  // 确认拒绝
  const handleReject = async () => {
    if (!rejectNote.trim()) {
      message.error(t('reject_reason_required') || '请填写拒绝原因');
      return;
    }

    setRejecting(true);
    try {
      const response = await adminAPI.rejectPreset(rejectingPreset.id, rejectNote);
      if (response.code === 0) {
        message.success(t('reject_success') || '已拒绝');
        setRejectModalVisible(false);
        setRejectNote('');
        setRejectingPreset(null);
        fetchPresets(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || t('reject_failed') || '拒绝失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('reject_failed') || '拒绝失败');
    } finally {
      setRejecting(false);
    }
  };

  const openPreviewDrawer = (preset) => {
    setPreviewPreset(preset);
    setPreviewDrawerVisible(true);
  };

  const openCloneModal = (preset) => {
    setCloningPreset(preset);
    setCloneName(`${preset.name} (Clone)`);
    setCloneModalVisible(true);
  };

  // 过滤后的预设数据
  const filteredPresets = presets.filter(preset => {
    const searchLower = searchText.toLowerCase();
    const matchSearch = !searchText ||
      preset.name?.toLowerCase().includes(searchLower) ||
      preset.description?.toLowerCase().includes(searchLower) ||
      preset.system_prompt?.toLowerCase().includes(searchLower) ||
      preset.model?.toLowerCase().includes(searchLower);

    const matchPublic = isPublicFilter === null || preset.is_public === isPublicFilter;
    const matchUser = !userIdFilter || preset.user_id === userIdFilter;
    const matchApproval = approvalStatusFilter === null || preset.approval_status === approvalStatusFilter;

    return matchSearch && matchPublic && matchUser && matchApproval;
  });

  // 统计数据
  const stats = {
    total: presets.length,
    public: presets.filter(p => p.is_public).length,
    private: presets.filter(p => !p.is_public).length,
    totalUses: presets.reduce((sum, p) => sum + (p.use_count || 0), 0),
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: (id) => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
          {id}
        </Text>
      ),
    },
    {
      title: t('preset_name'),
      dataIndex: 'name',
      width: 180,
      ellipsis: true,
      render: (name, record) => (
        <Button type="link" onClick={() => openPreviewDrawer(record)}>
          {name}
        </Button>
      ),
    },
    {
      title: t('preset_description'),
      dataIndex: 'description',
      width: 200,
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">-</Text>,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      width: 150,
      render: (model) => model ? <Tag color="blue">{model}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: t('preset_temperature'),
      dataIndex: 'temperature',
      width: 100,
      render: (temp) => temp?.toFixed(1) || '0.7',
    },
    {
      title: t('preset_max_tokens'),
      dataIndex: 'max_tokens',
      width: 100,
      render: (tokens) => tokens || 2048,
    },
    {
      title: t('approval_status') || '审核状态',
      dataIndex: 'approval_status',
      width: 140,
      render: (status) => {
        const statusMap = {
          'pending': { color: 'gold', text: t('pending_review') || '待审核', icon: <ClockCircleOutlined /> },
          'approved': { color: 'green', text: t('approved') || '已通过', icon: <CheckCircleOutlined /> },
          'rejected': { color: 'red', text: t('rejected') || '已拒绝', icon: <CloseCircleOutlined /> },
        };
        const config = statusMap[status] || { color: 'default', text: status, icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: t('preset_is_public'),
      dataIndex: 'is_public',
      width: 100,
      render: (isPublic) => (
        <Tag color={isPublic ? 'green' : 'default'}>
          {isPublic ? t('yes') : t('no')}
        </Tag>
      ),
    },
    {
      title: t('preset_use_count'),
      dataIndex: 'use_count',
      width: 100,
      render: (count) => (
        <Tooltip title={t('total_uses')}>
          <span>{count || 0}</span>
        </Tooltip>
      ),
    },
    {
      title: t('preset_clone_count'),
      dataIndex: 'clone_count',
      width: 100,
      render: (count) => count || 0,
    },
    {
      title: t('user_id'),
      dataIndex: 'user_id',
      width: 120,
      ellipsis: true,
      render: (userId) => {
        const idStr = String(userId || '');
        return (
          <Tooltip title={userId}>
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 11 }}>
              {idStr ? idStr.substring(0, 12) + (idStr.length > 12 ? '...' : '') : '-'}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: t('preset_created_at'),
      dataIndex: 'created_at',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: t('actions'),
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openPreviewDrawer(record)}>
            {t('preview')}
          </Button>
          {record.approval_status === 'pending' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(record)}
                loading={approving === record.id}
                style={{ color: '#52c41a' }}
              >
                {t('approve') || '通过'}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => openRejectModal(record)}
                style={{ color: '#ff4d4f' }}
              >
                {t('reject') || '拒绝'}
              </Button>
            </>
          )}
          <Popconfirm
            title={t('confirm_delete_preset')}
            description={t('delete_preset_warning')}
            onConfirm={() => handleDeletePreset(record.id)}
            okText={t('confirm')}
            cancelText={t('cancel')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <AppstoreOutlined style={{ marginRight: 8 }} />
        {t('preset_management')}
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title={t('total_presets')} value={stats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title={t('public_presets')} value={stats.public} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title={t('private_presets')} value={stats.private} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title={t('total_uses')} value={stats.totalUses} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 搜索筛选栏 */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder={t('search_preset_placeholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          />
          <Input
            placeholder={t('filter_by_user_id')}
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
          <Select
            placeholder={t('preset_is_public')}
            value={isPublicFilter}
            onChange={setIsPublicFilter}
            allowClear
            style={{ width: 120 }}
            options={[
              { value: true, label: t('yes') },
              { value: false, label: t('no') },
            ]}
          />
          <Select
            placeholder={t('approval_status') || '审核状态'}
            value={approvalStatusFilter}
            onChange={setApprovalStatusFilter}
            allowClear
            style={{ width: 120 }}
            options={[
              { value: 'pending', label: t('pending_review') || '待审核' },
              { value: 'approved', label: t('approved') || '已通过' },
              { value: 'rejected', label: t('rejected') || '已拒绝' },
            ]}
          />
          {(searchText || userIdFilter || isPublicFilter !== null || approvalStatusFilter !== null) && (
            <Button
              type="link"
              onClick={() => {
                setSearchText('');
                setUserIdFilter('');
                setIsPublicFilter(null);
                setApprovalStatusFilter(null);
              }}
            >
              {t('clear_filters')}
            </Button>
          )}
          <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: 13 }}>
            {t('total')}: {filteredPresets.length}
          </span>
        </div>

        <Table
          columns={columns}
          dataSource={filteredPresets}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${t('total')}: ${total}`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          }}
          size="small"
        />
      </Card>

      {/* 预设详情抽屉 */}
      <Drawer
        title={`${t('preset_preview')}: ${previewPreset?.name || ''}`}
        placement="right"
        width={600}
        open={previewDrawerVisible}
        onClose={() => setPreviewDrawerVisible(false)}
      >
        {previewPreset && (
          <div>
            <Descriptions column={2} variant="bordered" size="small">
              <Descriptions.Item label={t('preset_name')} span={2}>
                <Text strong>{previewPreset.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_description')} span={2}>
                {previewPreset.description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                <Tag color="blue">{previewPreset.model || '-'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_temperature')}>
                {previewPreset.temperature?.toFixed(2) || '0.7'}
              </Descriptions.Item>
              <Descriptions.Item label="Top P">
                {previewPreset.top_p?.toFixed(2) || '1.0'}
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_max_tokens')}>
                {previewPreset.max_tokens || 2048}
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_is_public')}>
                <Tag color={previewPreset.is_public ? 'green' : 'default'}>
                  {previewPreset.is_public ? t('yes') : t('no')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('approval_status') || '审核状态'}>
                {(() => {
                  const statusMap = {
                    'pending': { color: 'gold', text: t('pending_review') || '待审核' },
                    'approved': { color: 'green', text: t('approved') || '已通过' },
                    'rejected': { color: 'red', text: t('rejected') || '已拒绝' },
                  };
                  const config = statusMap[previewPreset.approval_status] || { color: 'default', text: previewPreset.approval_status || 'unknown' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_use_count')}>
                {previewPreset.use_count || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('user_id')} span={2}>
                <Text copyable style={{ fontFamily: 'monospace' }}>
                  {previewPreset.user_id || '-'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_created_at')} span={2}>
                {previewPreset.created_at ? new Date(previewPreset.created_at).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('preset_updated_at')} span={2}>
                {previewPreset.updated_at ? new Date(previewPreset.updated_at).toLocaleString() : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>{t('system_prompt')}</Divider>
            <Card size="small" style={{ background: '#f5f5f5' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 12 }}>
                {previewPreset.prompt || previewPreset.system_prompt || '-'}
              </pre>
            </Card>
          </div>
        )}
      </Drawer>

      {/* 拒绝预设弹窗 */}
      <Modal
        title={t('reject_preset') || '拒绝预设'}
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectNote('');
          setRejectingPreset(null);
        }}
        confirmLoading={rejecting}
        okText={t('confirm')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {t('reject_reason') || '拒绝原因'} *
          </label>
          <TextArea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t('enter_reject_reason') || '请输入拒绝原因，告知用户如何修改'}
            rows={4}
          />
        </div>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t('reject_preset_tip') || '拒绝后预设将不会公开，用户可以查看拒绝原因并修改后重新提交审核'}
        </Paragraph>
      </Modal>

      {/* 克隆预设弹窗 */}
      <Modal
        title={t('clone_preset')}
        open={cloneModalVisible}
        onOk={handleClonePreset}
        onCancel={() => {
          setCloneModalVisible(false);
          setCloneName('');
          setCloningPreset(null);
        }}
        confirmLoading={cloning}
        okText={t('confirm')}
        cancelText={t('cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {t('preset_name')} *
          </label>
          <Input
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder={t('enter_preset_name')}
          />
        </div>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t('clone_preset_tip')}
        </Paragraph>
      </Modal>
    </div>
  );
};

export default Presets;
